import React, { useCallback, useEffect, useMemo, useState } from 'react'
import qs from 'qs'
import cn from 'classnames'
import debounce from 'lodash/debounce'
import invoke from 'lodash/invoke'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'

// import { useGoodSuggestion } from '@monorepo/hooks'
import { IGood } from '@monorepo/interfaces'
import { useComponentVisible } from '@monorepo/hooks/Util'
import { useSuggestionRating } from '@monorepo/search_module/hooks/use-suggestion-rating.hook'
import { useSuggestions } from '@monorepo/search_module/hooks/use-suggestions.hook'
import { getSearchUrl } from '@monorepo/core/helpers/getGoodUrl'
import { SuggetionsList } from './SuggetionsList'

import style from './style.module.scss'
import { useHistory, useLocation } from 'react-router'

const vinRe = /^[A-HJ-NPR-Z\d]{9}[A-HJ-NPR-Z\d]{2}[A-Z\d]{6}$/i

type PropsType = {
  debounceTime?: number
  isUseVin?: boolean
  onHide?(): void
}

export const SearchField: React.FC<PropsType> = ({
  debounceTime = 1000,
  isUseVin,
  onHide,
}) => {
  // const {
  //   query,
  //   loading,
  //   suggestions,
  //   handleChange: baseHandleChange,
  //   handleSelect,
  //   handleClear,
  // } = useGoodSuggestion()

  // const handleSubmit = useCallback(
  //   (event: React.FormEvent<HTMLFormElement>) => {
  //     event.preventDefault()
  //   },
  //   []
  // )
  // const handleChange = useCallback(
  //   (event: React.ChangeEvent<HTMLInputElement>) => {
  //     baseHandleChange(event.target.value)
  //   },
  //   []
  // )

  const {
    isComponentVisible,
    hideComponentHandler,
    showComponentHandler,
    componentRef,
  } = useComponentVisible(false)

  const history = useHistory()
  const location = useLocation()

  const data = useMemo<{ number?: string }>(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search]
  )

  const showHandler = useCallback(
    (event) => {
      invoke(event, 'target.select')
      showComponentHandler()
    },
    [showComponentHandler]
  )

  const hideHandler = useCallback(() => {
    onHide?.()
    hideComponentHandler()
  }, [hideComponentHandler, onHide])

  const [query, setQuery] = useState('')
  const [request, setRequest] = useState('')
  const [activeItemIdx, setActiveItemIdx] = useState<number>(0)

  const vin = useMemo(
    () =>
      isUseVin && query && vinRe.test(query.trim()) ? query.trim() : undefined,
    [isUseVin, query]
  )

  const updateRequest = useMemo(() => debounce(setRequest, debounceTime), [
    debounceTime,
  ])

  const queryChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value)
      setActiveItemIdx(0)
      updateRequest(event.target.value)
    },
    [updateRequest]
  )

  const clearHandler = useCallback(() => {
    setQuery('')
    setRequest('')
  }, [])

  useEffect(() => {
    if (typeof data.number === 'string') {
      setQuery(data.number)
      setRequest(data.number)
    }
  }, [data.number])

  const { data: suggestions, isFetching, isFetched } = useSuggestions(
    request ?? '',
    {
      strict: true,
      useFTS: false,
      enabled:
        isComponentVisible &&
        typeof request === 'string' &&
        request.length >= 2 &&
        !vin,
    }
  )

  const activeItem: IGood | undefined = useMemo(
    () => (suggestions ?? [])[activeItemIdx - 1],
    [activeItemIdx, suggestions]
  )

  const keypressHandler = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      showComponentHandler()
      if (event.code === 'ArrowDown') {
        setActiveItemIdx((current) =>
          Math.min(current + 1, suggestions?.length ?? 0)
        )
      }
      if (event.code === 'ArrowUp') {
        setActiveItemIdx((current) => Math.max(current - 1, 1))
      }
    },
    [showComponentHandler, suggestions]
  )

  const { updateHandler: updateRatingHandler } = useSuggestionRating()

  const selectHandler = useCallback(
    (good: IGood) => {
      updateRatingHandler(good)
      history.push(getSearchUrl(good))
    },
    [history, updateRatingHandler]
  )

  const submitHandler = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      hideHandler()

      if (vin) {
        history.push(`/catalogs/originals/vehicles?vin=${query}`)
        return
      }
      if (activeItem || suggestions?.length === 1) {
        const good = activeItem || suggestions?.[0]
        selectHandler(good)
        return
      }
      if (query) {
        history.push(`/search?number=${query}`)
        return
      }
    },
    [activeItem, hideHandler, history, query, selectHandler, suggestions, vin]
  )

  return (
    <form
      className={cn(style.container, 'dgwt-wcas-search-form')}
      ref={componentRef}
      role="search"
      onSubmit={submitHandler}
    >
      <div className="dgwt-wcas-sf-wrapp">
        <label className="screen-reader-text">Поиск товаров</label>

        <input
          type="search"
          className={cn(style.inputField, {
            [style.inputFieldOpen]: isComponentVisible,
          })}
          name="query"
          autoComplete="off"
          placeholder={
            isUseVin ? 'Номер запчасти или VIN автомобиля' : 'Номер запчасти...'
          }
          onChange={queryChangeHandler}
          onKeyDown={keypressHandler}
          value={query}
          onClick={showHandler}
          onBlur={hideComponentHandler}
        />
        <FontAwesomeIcon icon={faSearch} className="dgwt-wcas-ico-magnifier" />

        {query.length > 0 && (
          <div
            className={cn('dgwt-wcas-preloader', 'dgwt-wcas-close', {
              'dgwt-wcas-inner-preloader': isFetching,
            })}
            onClick={clearHandler}
          />
        )}
        <SuggetionsList
          className={cn(style.suggestionList, {
            [style.suggestionListOpen]: isComponentVisible,
          })}
          query={query}
          loading={isFetching}
          suggestions={suggestions}
          handleSelect={selectHandler}
          isFetched={isFetched}
        />
      </div>
    </form>
  )
}
